import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, Link } from "react-router-dom";
import { notify } from "../../utilities/toast";
import { FaEye, FaEyeSlash, FaCheckCircle, FaLock } from "react-icons/fa";
import Loader from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import { changePassword } from "../../services/user-services";

interface ResetPasswordForm {
    new_password: string;
    confirmPassword: string;
}

const ResetPassword = () => {
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isPasswordReset, setIsPasswordReset] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { uidb64, token } = useParams();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<ResetPasswordForm>();

    const password = watch('new_password');

    const validatePassword = (value: string) => {
        if (value.length < 8) {
            return t('resetPassword.passwordTooShort');
        }
        if (!/[A-Z]/.test(value)) {
            return t('resetPassword.passwordNeedsUppercase');
        }
        if (!/[a-z]/.test(value)) {
            return t('resetPassword.passwordNeedsLowercase');
        }
        if (!/[0-9]/.test(value)) {
            return t('resetPassword.passwordNeedsNumber');
        }
        if (!/[.!@#$%^&*]/.test(value)) {
            return t('resetPassword.passwordNeedsSpecial');
        }
        return true;
    };

    const onSubmit = async (data: ResetPasswordForm) => {
        try {
            if (!uidb64 || !token) {
                notify(t('resetPassword.invalidLink'), 'error');
                return;
            }

            const response = await changePassword(
                {
                    uidb64,
                    token,
                    new_password: data.new_password
                },
                setLoading
            );

            if (response.status === 200) {
                setIsPasswordReset(true);
            } else {
                if (response.error && typeof response.error === 'object' && 'details' in response.error) {
                    const details = (response.error as {details: string[]}).details;
                    details.forEach(error => notify(error, 'error'));
                } else {
                    notify(response.error as string || t('resetPassword.error'), 'error');
                }
            }
        } catch (error) {
            console.error('Password reset error:', error);
            notify(t('resetPassword.error'), 'error');
        }
    };

    if (isPasswordReset) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-100 dark:bg-black">
                <div className="max-w-md w-full space-y-8 bg-white dark:bg-slate-900 p-8 rounded-lg shadow-lg text-center">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900">
                            <FaCheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {t('resetPassword.success')}
                        </h2>
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900">
                            <FaLock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-zinc-400">
                            {t('resetPassword.successMessage')}
                        </p>
                    </div>
                    <div className="mt-6">
                        <Link
                            to="/login"
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {t('resetPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-100 dark:bg-black">
            <div className="max-w-md w-full space-y-8 bg-white dark:bg-slate-900 p-8 rounded-lg shadow-lg">
                <div>
                    <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                        {t('resetPassword.title')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600 dark:text-zinc-400">
                        {t('resetPassword.description')}
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 dark:text-zinc-400">
                            {t('resetPassword.newPassword')}
                        </label>
                        <div className="mt-1 relative">
                            <input
                                {...register("new_password", {
                                    required: t('resetPassword.passwordRequired'),
                                    validate: validatePassword
                                })}
                                type={showPassword ? "text" : "password"}
                                id="new_password"
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            {showPassword ? (
                                <FaEye
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
                                    onClick={() => setShowPassword(!showPassword)}
                                />
                            ) : (
                                <FaEyeSlash
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
                                    onClick={() => setShowPassword(!showPassword)}
                                />
                            )}
                        </div>
                        {errors.new_password && (
                            <p className="mt-2 text-sm text-red-600">{errors.new_password.message}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-zinc-400">
                            {t('resetPassword.confirmPassword')}
                        </label>
                        <div className="mt-1 relative">
                            <input
                                {...register("confirmPassword", {
                                    required: t('resetPassword.confirmPasswordRequired'),
                                    validate: (value) =>
                                        value === watch("new_password") || t('resetPassword.passwordsMatch')
                                })}
                                type={showConfirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            {showConfirmPassword ? (
                                <FaEye
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                />
                            ) : (
                                <FaEyeSlash
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                />
                            )}
                        </div>
                        {errors.confirmPassword && (
                            <p className="mt-2 text-sm text-red-600">{errors.confirmPassword.message}</p>
                        )}
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {loading ? <Loader /> : t('resetPassword.submit')}
                        </button>
                    </div>

                    <div className="text-center">
                        <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                            {t('resetPassword.backToLogin')}
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword; 